import React from 'react';

export type HeadingSettings = {
  text?: string;
  color?: string; // tailwind class e.g. 'text-gray-900'
  fontFamily?: string;
};
export type ButtonSettings = {
  label?: string;
  textColor?: string; // hex or css
  bgMode?: 'solid'|'gradient';
  bgColor?: string;
  gradFrom?: string;
  gradTo?: string;
  radius?: number;
  shadow?: boolean;
  fontFamily?: string;
};

type Props = {
  top: number;
  left: number;
  kind: 'heading' | 'button';
  value: HeadingSettings | ButtonSettings;
  onChange: (v: HeadingSettings | ButtonSettings) => void;
  onClose: () => void;
  panelRef?: React.RefObject<HTMLDivElement | null>;
};

const colorChoices = [
  'text-gray-900',
  'text-white',
  'text-blue-600',
  'text-purple-600',
  'text-red-600',
  'text-emerald-600',
  'text-amber-600',
];

const fontChoices: { label: string; stack: string }[] = [
  { label: 'System (Inter/Sans)', stack: "Inter, ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'Apple Color Emoji', 'Segoe UI Emoji'" },
  { label: 'Serif (Georgia)', stack: "Georgia, 'Times New Roman', Times, serif" },
  { label: 'Monospace', stack: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace" },
  { label: 'Playfair', stack: "'Playfair Display', Georgia, 'Times New Roman', Times, serif" },
  { label: 'Poppins', stack: "Poppins, Inter, ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial" },
  { label: 'Source Serif', stack: "'Source Serif Pro', Georgia, 'Times New Roman', Times, serif" },
  { label: 'Roboto', stack: "Roboto, Inter, ui-sans-serif, system-ui, -apple-system, 'Segoe UI', 'Helvetica Neue', Arial" },
];

const CellOverlaySettingsPopup: React.FC<Props> = ({ top, left, kind, value, onChange, onClose, panelRef }) => {
  return (
    <div ref={panelRef} className="fixed z-[2150] w-80 max-h-[80vh] overflow-y-auto rounded-xl border border-gray-200 bg-white/95 backdrop-blur shadow-xl" style={{ top, left }} onClick={(e)=> e.stopPropagation()}>
      <div className="flex items-center justify-between px-3 pt-3">
        <div className="text-sm font-medium text-gray-900">{kind === 'heading' ? 'Heading' : 'Button'} settings</div>
        <button className="h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-gray-50" onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); onClose(); }} title="Close" aria-label="Close settings">
          <svg className="h-4 w-4 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
        </button>
      </div>
      <div className="px-3 pb-3 space-y-3">
        {kind === 'heading' && (
          <>
            <label className="text-xs font-medium text-gray-600">Text</label>
            <input
              value={(value as any).text ?? ''}
              onChange={(e)=> onChange({ ...(value as any), text: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter heading text"
            />
            <div>
              <div className="text-xs font-medium text-gray-600 mb-1">Font</div>
              <select
                className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                value={(value as any).fontFamily ?? ''}
                onChange={(e)=> onChange({ ...(value as any), fontFamily: e.target.value })}
              >
                <option value="">Default</option>
                {fontChoices.map(f => (
                  <option key={f.label} value={f.stack}>{f.label}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-600 mb-1">Color</div>
              <div className="flex flex-wrap gap-2">
                {colorChoices.map(c => (
                  <button
                    key={c}
                    className={`h-7 px-2 rounded-lg border ${((value as any).color||'text-gray-900')===c?'border-blue-300 bg-blue-50':'border-gray-200 bg-white'} text-sm`}
                    onClick={(e)=> { e.preventDefault(); onChange({ ...(value as any), color: c }); }}
                  >
                    <span className={`${c}`}>Aa</span>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
        {kind === 'button' && (
          <>
            <label className="text-xs font-medium text-gray-600">Label</label>
            <input
              value={(value as any).label ?? ''}
              onChange={(e)=> onChange({ ...(value as any), label: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Button label"
            />
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div>
                <label className="text-xs font-medium text-gray-600">Font</label>
                <select
                  className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  value={(value as any).fontFamily ?? ''}
                  onChange={(e)=> onChange({ ...(value as any), fontFamily: e.target.value })}
                >
                  <option value="">Default</option>
                  {fontChoices.map(f => (
                    <option key={f.label} value={f.stack}>{f.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Text color</label>
                <div className="relative">
                  <input
                    type="text"
                    value={(value as any).textColor ?? '#ffffff'}
                    onChange={(e)=> onChange({ ...(value as any), textColor: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 pr-10 px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="#ffffff"
                  />
                  <label className="absolute right-1.5 top-1.5 h-6 w-6 rounded-md border border-gray-300 shadow-sm cursor-pointer" style={{ background: (value as any).textColor ?? '#ffffff' }}>
                    <input
                      type="color"
                      value={(value as any).textColor ?? '#ffffff'}
                      onChange={(e)=> onChange({ ...(value as any), textColor: e.target.value })}
                      className="opacity-0 w-0 h-0 absolute"
                    />
                  </label>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Background</label>
                <select
                  className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  value={(value as any).bgMode ?? 'solid'}
                  onChange={(e)=> onChange({ ...(value as any), bgMode: e.target.value as any })}
                >
                  <option value="solid">Solid</option>
                  <option value="gradient">Gradient</option>
                  <option value="invert">Invert</option>
                </select>
              </div>
            </div>
            {((value as any).bgMode ?? 'solid') === 'solid' ? (
              <div className="mt-2">
                <label className="text-xs font-medium text-gray-600">Solid color</label>
                <div className="relative">
                  <input
                    type="text"
                    value={(value as any).bgColor ?? '#2563eb'}
                    onChange={(e)=> onChange({ ...(value as any), bgColor: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 pr-10 px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="#2563eb"
                  />
                  <label className="absolute right-1.5 top-1.5 h-6 w-6 rounded-md border border-gray-300 shadow-sm cursor-pointer" style={{ background: (value as any).bgColor ?? '#2563eb' }}>
                    <input
                      type="color"
                      value={(value as any).bgColor ?? '#2563eb'}
                      onChange={(e)=> onChange({ ...(value as any), bgColor: e.target.value })}
                      className="opacity-0 w-0 h-0 absolute"
                    />
                  </label>
                </div>
              </div>
            ) : ((value as any).bgMode ?? 'solid') === 'gradient' ? (
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <label className="text-xs font-medium text-gray-600">Gradient from</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={(value as any).gradFrom ?? '#2563eb'}
                      onChange={(e)=> onChange({ ...(value as any), gradFrom: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 pr-10 px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="#2563eb"
                    />
                    <label className="absolute right-1.5 top-1.5 h-6 w-6 rounded-md border border-gray-300 shadow-sm cursor-pointer" style={{ background: (value as any).gradFrom ?? '#2563eb' }}>
                      <input
                        type="color"
                        value={(value as any).gradFrom ?? '#2563eb'}
                        onChange={(e)=> onChange({ ...(value as any), gradFrom: e.target.value })}
                        className="opacity-0 w-0 h-0 absolute"
                      />
                    </label>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">Gradient to</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={(value as any).gradTo ?? '#4f46e5'}
                      onChange={(e)=> onChange({ ...(value as any), gradTo: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 pr-10 px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="#4f46e5"
                    />
                    <label className="absolute right-1.5 top-1.5 h-6 w-6 rounded-md border border-gray-300 shadow-sm cursor-pointer" style={{ background: (value as any).gradTo ?? '#4f46e5' }}>
                      <input
                        type="color"
                        value={(value as any).gradTo ?? '#4f46e5'}
                        onChange={(e)=> onChange({ ...(value as any), gradTo: e.target.value })}
                        className="opacity-0 w-0 h-0 absolute"
                      />
                    </label>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-2 text-xs text-gray-500">Background will invert whatever is behind the button.</div>
            )}
            <div className="grid grid-cols-2 gap-3 items-end mt-2">
              <div>
                <label className="text-xs font-medium text-gray-600">Edge radius</label>
                <input
                  type="range" min={0} max={128}
                  value={(value as any).radius ?? 10}
                  onChange={(e)=> onChange({ ...(value as any), radius: parseInt(e.target.value,10)||0 })}
                  className="w-full accent-blue-600"
                />
              </div>
              <div className="flex items-center justify-between">
                <label className="inline-flex items-center gap-2 text-xs font-medium text-gray-600">
                  <input type="checkbox" checked={!!(value as any).shadow} onChange={(e)=> onChange({ ...(value as any), shadow: e.target.checked })} />
                  Drop shadow
                </label>
                <label className="inline-flex items-center gap-2 text-xs font-medium text-gray-600">
                  <input type="checkbox" checked={!!(value as any).capsule} onChange={(e)=> onChange({ ...(value as any), capsule: e.target.checked })} />
                  Capsule
                </label>
              </div>
            </div>

            <div className="mt-2">
              <label className="text-xs font-medium text-gray-600">Height</label>
              <input
                type="range" min={24} max={120}
                value={(value as any).height ?? 36}
                onChange={(e)=> onChange({ ...(value as any), height: parseInt(e.target.value,10)||36 })}
                className="w-full accent-blue-600"
              />
            </div>
            <div className="mt-2">
              <label className="text-xs font-medium text-gray-600">Padding X</label>
              <input
                type="range" min={8} max={48}
                value={(value as any).paddingX ?? 16}
                onChange={(e)=> onChange({ ...(value as any), paddingX: parseInt(e.target.value,10)||16 })}
                className="w-full accent-blue-600"
              />
            </div>

            <div className="mt-2">
              <label className="text-xs font-medium text-gray-600">Text size</label>
              <input
                type="range" min={12} max={32}
                value={(value as any).fontSize ?? 14}
                onChange={(e)=> onChange({ ...(value as any), fontSize: parseInt(e.target.value,10)||14 })}
                className="w-full accent-blue-600"
              />
            </div>
            <label className="inline-flex items-center gap-2 text-xs font-medium text-gray-600 mt-2">
              <input type="checkbox" checked={!!(value as any).glow} onChange={(e)=> onChange({ ...(value as any), glow: e.target.checked })} />
              Glow
            </label>

            <div className="mt-2">
              <label className="text-xs font-medium text-gray-600">Outline</label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <select
                    className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                    value={(value as any).outlineMode ?? 'color'}
                    onChange={(e)=> onChange({ ...(value as any), outlineMode: e.target.value })}
                  >
                    <option value="color">Color</option>
                    <option value="gradient">Gradient</option>
                    <option value="invert">Invert</option>
                  </select>
                </div>
                {((value as any).outlineMode ?? 'color') === 'color' && (
                  <div className="relative">
                    <input
                      type="text"
                      value={(value as any).outlineColor ?? ''}
                      onChange={(e)=> onChange({ ...(value as any), outlineColor: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 pr-10 px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="#ffffff"
                    />
                    <label className="absolute right-1.5 top-1.5 h-6 w-6 rounded-md border border-gray-300 shadow-sm cursor-pointer" style={{ background: (value as any).outlineColor ?? (value as any).bgColor ?? '#ffffff' }}>
                      <input
                        type="color"
                        value={(value as any).outlineColor ?? '#ffffff'}
                        onChange={(e)=> onChange({ ...(value as any), outlineColor: e.target.value })}
                        className="opacity-0 w-0 h-0 absolute"
                      />
                    </label>
                  </div>
                )}
                {((value as any).outlineMode ?? 'color') === 'gradient' && (
                  <>
                    <div className="relative">
                      <input
                        type="text"
                        value={(value as any).outlineGradFrom ?? '#2563eb'}
                        onChange={(e)=> onChange({ ...(value as any), outlineGradFrom: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 pr-10 px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="#2563eb"
                      />
                      <label className="absolute right-1.5 top-1.5 h-6 w-6 rounded-md border border-gray-300 shadow-sm cursor-pointer" style={{ background: (value as any).outlineGradFrom ?? '#2563eb' }}>
                        <input
                          type="color"
                          value={(value as any).outlineGradFrom ?? '#2563eb'}
                          onChange={(e)=> onChange({ ...(value as any), outlineGradFrom: e.target.value })}
                          className="opacity-0 w-0 h-0 absolute"
                        />
                      </label>
                    </div>
                    <div className="relative">
                      <input
                        type="text"
                        value={(value as any).outlineGradTo ?? '#4f46e5'}
                        onChange={(e)=> onChange({ ...(value as any), outlineGradTo: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 pr-10 px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="#4f46e5"
                      />
                      <label className="absolute right-1.5 top-1.5 h-6 w-6 rounded-md border border-gray-300 shadow-sm cursor-pointer" style={{ background: (value as any).outlineGradTo ?? '#4f46e5' }}>
                        <input
                          type="color"
                          value={(value as any).outlineGradTo ?? '#4f46e5'}
                          onChange={(e)=> onChange({ ...(value as any), outlineGradTo: e.target.value })}
                          className="opacity-0 w-0 h-0 absolute"
                        />
                      </label>
                    </div>
                  </>
                )}
                {((value as any).outlineMode ?? 'color') === 'invert' && (
                  <div className="text-xs text-gray-500 self-center">Inverts background behind the ring.</div>
                )}
              </div>
            </div>
            <div className="mt-2">
              <label className="text-xs font-medium text-gray-600">Outline width</label>
              <input
                type="range" min={0} max={24}
                value={(value as any).outlineWidth ?? 0}
                onChange={(e)=> onChange({ ...(value as any), outlineWidth: parseInt(e.target.value,10)||0 })}
                className="w-full accent-blue-600"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CellOverlaySettingsPopup;
